import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createRawDocument } from '../api/documents';
import { configApi } from '../api/config';
import { useAsyncData } from '../hooks/useAsyncData';
import { MARITIME_DOCUMENT_TYPE_OPTIONS, type DocumentType } from '../types/api';

const CONTENT_TYPE_OPTIONS = ['application/edi', 'application/xml', 'text/plain'];

// Direct sibling of DocumentGenerateForm/Generate document: same two-step safety model (this
// step only creates+validates the document; delivery still requires the separate, confirmed
// Resend action on the Document Detail page) and the same panel/form-grid layout, but takes
// the message body itself instead of a JSON source payload -- no builder is invoked, so what's
// typed below is exactly what gets stored and, on Resend, sent to the partner.
export function ComposeRawMessageForm() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [documentType, setDocumentType] = useState<DocumentType>('CUSCAR');
  const [partnerId, setPartnerId] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [filename, setFilename] = useState('');
  const [contentType, setContentType] = useState(CONTENT_TYPE_OPTIONS[0]);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: partners } = useAsyncData(() => configApi.partners.list({ page: 1, pageSize: 100, isActive: true }), []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    try {
      setIsSubmitting(true);
      if (!body.trim()) {
        throw new Error('Message content is required.');
      }
      const created = await createRawDocument({
        documentType,
        partnerId,
        referenceId,
        correlationId,
        filename,
        contentType,
        body,
      });
      navigate({ pathname: `../documents/${created.id}`, search });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Creation failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <h2>Compose raw message</h2>
        <p>Write a message body directly (EDIFACT, XML, or plain text) and send it to a partner as-is, with no builder or field mapping in between.</p>
      </div>
      <form className="form-grid" onSubmit={handleSubmit}>
        <label>
          <span>Document type</span>
          <select value={documentType} onChange={(event) => setDocumentType(event.target.value as DocumentType)}>
            {MARITIME_DOCUMENT_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Partner</span>
          <select value={partnerId} required onChange={(event) => setPartnerId(event.target.value)}>
            <option value="">Select a partner</option>
            {(partners?.data ?? []).map((partner) => (
              <option key={partner.id} value={partner.id}>{partner.name} ({partner.code})</option>
            ))}
          </select>
        </label>
        <label>
          <span>Reference ID</span>
          <input required value={referenceId} onChange={(event) => setReferenceId(event.target.value)} />
        </label>
        <label>
          <span>Correlation ID</span>
          <input required value={correlationId} onChange={(event) => setCorrelationId(event.target.value)} />
        </label>
        <label>
          <span>File name</span>
          <input required value={filename} onChange={(event) => setFilename(event.target.value)} placeholder="CUSCAR_manual_test.EDI" />
        </label>
        <label>
          <span>Content type</span>
          <select value={contentType} onChange={(event) => setContentType(event.target.value)}>
            {CONTENT_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>

        <div className="full-width setup-callout">
          <div>
            <strong>No builder is used here</strong>
            <span>The text below is stored and delivered exactly as typed -- it is not run through the {documentType} builder, and this document cannot later be rebuilt from a source payload.</span>
          </div>
        </div>

        <label className="full-width">
          <span>Message content</span>
          <textarea
            required
            rows={18}
            className="mono"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Paste or write the raw message content."
          />
        </label>

        {error ? <div className="form-error full-width">{error}</div> : null}
        <div className="full-width form-actions">
          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create'}
          </button>
        </div>
      </form>
    </section>
  );
}
